import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Row, Col, Table, OverlayTrigger, Tooltip } from 'react-bootstrap'
import ModalContainer from '../../ema_module/components/ModalContainer'
import Loader from '../../participant_module/components/Loader'
import Message from '../../participant_module/components/Message'

import { getScheduledJitaiTaskList } from '../actions/jitaiActions'
import { JITAI_SCHEDULED_TASK_LIST_RESET } from '../constants/jitaiConstants'
import { createJitaiTasks } from '../../participant_module/actions/participantActions'
import { CREATE_JITAI_TASKS_RESET } from '../../participant_module/constants/participantConstants'

export const ScheduledJitaiTask = ({ email, studyId, participantId }) => {
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo, logout } = userLogin

  const jitaiScheduledTaskList = useSelector(
    (state) => state.jitaiScheduledTaskList
  )
  const {
    loading: loadingJitaiTasks,
    error: errorJitaiTasks,
    jitaiTasks: loadedJitaiTasks,
    success: successJitaiTasks,
  } = jitaiScheduledTaskList

  const createJitaiTasksState = useSelector((state) => state.createJitaiTasks)
  const {
    loading: loadingCreateJitaiTasks,
    error: errorCreateJitaiTasks,
    success: successCreateJitaiTasks,
    result: createJitaiTasksResult,
  } = createJitaiTasksState

  const dispatch = useDispatch()

  const [show, setShow] = useState(false)

  useEffect(() => {
    if (show && successCreateJitaiTasks) {
      dispatch({ type: CREATE_JITAI_TASKS_RESET })
      dispatch({ type: JITAI_SCHEDULED_TASK_LIST_RESET })
      dispatch(getScheduledJitaiTaskList(studyId, participantId))
    }
  }, [show, successCreateJitaiTasks, dispatch, studyId, participantId])

  const handleOpen = () => {
    let now = new Date()
    if (userInfo && userInfo.expires > now && !logout) {
      if (
        studyId &&
        participantId &&
        !successJitaiTasks &&
        !loadingJitaiTasks &&
        !errorJitaiTasks
      ) {
        dispatch(getScheduledJitaiTaskList(studyId, participantId))
      }
    }
    setShow(true)
  }

  const handleClose = () => {
    dispatch({ type: JITAI_SCHEDULED_TASK_LIST_RESET })
    if (errorCreateJitaiTasks) {
      dispatch({ type: CREATE_JITAI_TASKS_RESET })
    }
    setShow(false)
  }

  const handleCreateJitaiTasks = () => {
    dispatch(createJitaiTasks(studyId, participantId))
  }

  const headingStyle = {
    fontSize: '1.2rem',
  }

  return (
    <>
      <Button variant='primary' className='btn-sm m-1' onClick={handleOpen}>
        Scheduled JITAI Tasks
      </Button>

      <ModalContainer
        title={`Scheduled JITAI Tasks`}
        show={show}
        handleClose={handleClose}
      >
        {errorJitaiTasks && (
          <Message variant='danger'>{errorJitaiTasks}</Message>
        )}

        {loadingJitaiTasks ? (
          <Loader />
        ) : (
          <>
            {successJitaiTasks && (
              <>
                <div className='d-flex justify-content-between align-items-center'>
                  <h2 style={headingStyle} className='mb-0'>
                    {`Participant: ${email}`}
                  </h2>
                  <div className='d-flex align-items-center'>
                    <Button
                      variant='primary'
                      className='btn-sm'
                      disabled={loadingCreateJitaiTasks}
                      onClick={handleCreateJitaiTasks}
                    >
                      {loadingCreateJitaiTasks
                        ? 'Creating...'
                        : 'Create JITAI Tasks'}
                    </Button>
                    <OverlayTrigger
                      placement='bottom'
                      overlay={
                        <Tooltip id='tooltip-create-jitai-tasks'>
                          This will generate JITAI tasks for this participant
                          for the next 24 hours based on the study's JITAI
                          settings. A task will only be created if no existing
                          task matches the same participant, JITAI setting, and
                          notification time. If a matching task already exists,
                          it will be skipped.
                        </Tooltip>
                      }
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          backgroundColor: '#333',
                          color: '#fff',
                          fontSize: '13px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          marginLeft: '6px',
                        }}
                      >
                        ?
                      </span>
                    </OverlayTrigger>
                  </div>
                </div>

                {errorCreateJitaiTasks && (
                  <Message variant='danger'>
                    Failed to create JITAI tasks: {errorCreateJitaiTasks}
                  </Message>
                )}

                <Row>
                  {loadedJitaiTasks && loadedJitaiTasks.length > 0 ? (
                    <>
                      <Col md={12}>
                        <Table
                          bordered
                          hover
                          responsive
                          className='table-sm'
                          style={{ marginTop: '20px' }}
                        >
                          <thead>
                            <tr>
                              <th>Task Name</th>
                              <th>Task ID</th>
                              <th>Notification Time</th>
                              <th>Timezone</th>
                              <th>Status</th>
                              <th>Push Notification Scheduled?</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadedJitaiTasks.map((task) => (
                              <tr key={task._id}>
                                <td>{task.task_name || '—'}</td>
                                <td style={{ fontSize: '0.85rem' }}>
                                  {task._id}
                                </td>
                                <td>{task.notification_datetime}</td>
                                <td>{task.timezone}</td>
                                <td>{task.status}</td>
                                <td>
                                  {task.has_push_schedule ? (
                                    <span style={{ color: 'green' }}>Yes</span>
                                  ) : (
                                    <span style={{ color: 'red' }}>No</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Col>
                    </>
                  ) : (
                    <Col>
                      <p>
                        There is no scheduled JITAI task for this participant.
                      </p>
                    </Col>
                  )}
                </Row>
              </>
            )}
          </>
        )}
      </ModalContainer>
    </>
  )
}

export default ScheduledJitaiTask
